/**
 * Helper for loading state
 */
class LoaderState
{
	/**
	 * @param vueObject object The object contaning the loading property
	 */
	constructor(vueObject)
	{
		this.vueObject = vueObject;
	}

	/**
	 * Change the state of loading of the given data
	 * @param loadingState bool
	 */
	setLoadingState(loadingState)
	{
		this.vueObject.loading = loadingState;
	}

	/**
	 * Is the given namespace is in loading mode
	 * @returns bool
	 */
	isLoading()
	{
		return this.vueObject.loading;
	}
}

/**
 * Class to help loading the data, can be use statically or not
 */
class ApiMonizze
{
	/**
	 * @param loaderState LoaderState
	 */
	constructor(loaderState)
	{
		this.loaderState = loaderState;
		this.validError = {};
		/**
		 * This attribute switch ApiMonizze to single request mode.
		 * Therefore, if a second request is sent, the first one will be cancelled
		 *
		 * To enable/disable this behavior, please use enableSingleRequest() and disableSingleRequest() methods
		 *
		 * @type {boolean}
		 */
		this._isSingleRequestMode = false;
		/**
		 * This variable contains the cancel token used internally by Axios to… cancel the request :P
		 * You shouldn't have to touch it.
		 * @type {object}
		 */
		this._internalCancelToken = null;
	}

	addValidErrors(statusCodes)
	{
		for(const statusCode of statusCodes)
		{
			this.validError[statusCode] = true;
		}

		return this;
	}

	/**
	 * Load data with a get from the api
	 * @param url string
	 * @param treatDataCallback closure received the data from the api and return it to be stored
	 *
	 * @returns {Promise<T>}
	 */
	getDataFromApi(url, treatDataCallback)
	{
		this._prefetchSetup();

		return axios
			.get(url, this._defaultAxiosConfig())
			.then(
				this._treatDataFromApi(treatDataCallback),
				this._treatErrorFromApi
			);
	}

	/**
	 * Perform a DELETE call to the API
	 *
	 * @param url string
	 */
	deleteDataFromApi(url, treatDataCallback)
	{
		this._prefetchSetup();

		return axios
			.delete(url, this._defaultAxiosConfig())
			.then(
				this._treatDataFromApi(treatDataCallback),
				(error) => this._treatErrorFromApi(error),
			)
	}

	/**
	 * Load data with a post from the api
	 * @param url string
	 * @param data mixed
	 * @param treatDataCallback closure received the data from the api and return it to be stored
	 *
	 * @returns {Promise<T>}
	 */
	postDataFromApi(url, data, treatDataCallback)
	{
		this._prefetchSetup();

		return axios
			.post(url, data, this._defaultAxiosConfig())
			.then(
				this._treatDataFromApi(treatDataCallback),
				(error) => this._treatErrorFromApi(error),
			);
	}

	/**
	 * Put data with PUT method to API
	 *
	 * @param url
	 * @param data
	 * @param treatDataCallback
	 * @returns {Q.Promise<any> | * | Promise<T | never>}
	 */
	putDataToApi(url, data, treatDataCallback)
	{
		this._prefetchSetup();

		return axios
			.put(url, data, this._defaultAxiosConfig())
			.then(
				this._treatDataFromApi(treatDataCallback),
				(error) => this._treatErrorFromApi(error),
			)
		;
	}

	patchDataFromApi(url, data, treatDataCallback)
	{
		this._prefetchSetup();

		return axios
			.patch(url, data, this._defaultAxiosConfig())
			.then(
				this._treatDataFromApi(treatDataCallback),
				(error) => this._treatErrorFromApi(error),
			)
		;
	}

	/**
	 * Load the data from storage than from api
	 * @param axiosOption hash see defaultAxiosOption
	 * @param loadCallback closure receive the data from the storage
	 * @param treatDataCallback closure received the data from the api and return it to be stored
	 */
	loadData(axiosOption, loadCallback, treatDataCallback)
	{
		axiosOption = this._defaultAxiosOption(axiosOption);

		if ( treatDataCallback === undefined || treatDataCallback === null)
		{
			treatDataCallback = loadCallback;
		}

		switch (axiosOption.method)
		{
			case 'post':
				return this.postDataFromApi(axiosOption.url, axiosOption.data, treatDataCallback);
			case 'put':
				return this.putDataToApi(axiosOption.url, axiosOption.data, treatDataCallback);
			case 'patch':
				return this.patchDataFromApi(axiosOption.url, axiosOption.data, treatDataCallback);
			case 'delete':
				return this.deleteDataFromApi(axiosOption.url, treatDataCallback);
			default:
				return this.getDataFromApi(axiosOption.url, treatDataCallback);
		}
	}

	enableSingleRequest()
	{
		this._isSingleRequestMode = true;
	}

	disableSingleRequest()
	{
		this._isSingleRequestMode = false;
	}

	_createCancelToken()
	{
		this._internalCancelToken = axios.CancelToken.source();
	}

	_cancelPreviousRequest()
	{
		if (null === this._internalCancelToken)
		{
			return;
		}
		this._internalCancelToken.cancel('');
	}

	_prefetchSetup()
	{
		if (this.loaderState)
		{
			this.loaderState.setLoadingState(true);
		}
		cleanFlashAlert();
	}

	/**
	 * Get a the default data structure for axios option for the 'loadData' method
	 *
	 * @param option hash of values to define
	 * @returns {*}
	 * @private
	 */
	_defaultAxiosOption(option)
	{
		option.method = option.method || 'get';
		option.url = option.url || '';
		option.data = option.data || [];

		return option;
	}

	_defaultAxiosConfig()
	{
		const validError = this.validError;

		const axiosConfig = {
			headers: axiosHeaderForMonizzeApi,
			params: axiosParamsForMonizzeApi,
			validateStatus(status)
			{
				return (status >= 200 && status < 300) || validError[status] === true;
			},
		};

		if (this._isSingleRequestMode)
		{
			this._cancelPreviousRequest();
			this._createCancelToken();
			axiosConfig.cancelToken = this._internalCancelToken.token;
		}

		return axiosConfig;
	}

	_treatDataFromApi(treatDataCallback)
	{
		const self = this;

		return function (response)
		{
			const data = treatDataCallback(response.data.data, response.data);
			if (self.loaderState)
			{
				self.loaderState.setLoadingState(false);
			}

			/**
			 * Call finished.  Destroying cancel token
			 */
			if (self._isSingleRequestMode)
			{
				self._internalCancelToken = null;
			}
		};
	}

	_treatErrorFromApi(error)
	{
		/*
		 * New call is performed BEFORE cancel call.
		 * Because of that, Cancel call must not
		 * interrupt loader, because the loader must be
		 * shown until the last call has ended.
		 *
		 * If error is caused by a CANCEL call,
		 * this is not an error
		 */
		if (error.__CANCEL__)
		{
			return Promise.resolve();
		}

		if ( this && this.loaderState )
		{
			this.loaderState.setLoadingState(false);
		}

		return Promise.reject(error);
	}
}

/**
 * Helper class to transform a json array of translations to be used by key, with fallback for missing translations
 */
class TranslationsFactory
{
	create(translations, prefix)
	{
		return new Proxy(
			this.implode(translations),
			{
				get: (target, prop, receiver) => {
					if (target.hasOwnProperty(prop))
					{
						return target[prop];
					}
					return prefix ? prefix + '.' + prop.toString() : prop;
				}
			}
		);
	}

	implode(items, prefix)
	{
		let implodedItems = {};
		for (let key in items)
		{
			let item = items[key];
			let nextKey = prefix ? prefix + '.' + key : key;
			if (typeof item === 'object')
			{
				implodedItems = Object.assign(implodedItems, this.implode(item, nextKey));
			}
			else
			{
				implodedItems[nextKey] = item;
			}
		}
		return implodedItems;
	}
}

export {
	LoaderState,
	ApiMonizze,
	TranslationsFactory
};
